/**
 * Table arithmetic and seat-error mapping.
 *
 * A "table" is a ticket tier sold as a package of N seats. The backend stores
 * `price` PER SEAT and sets `tierIncrement` equal to `tableCapacity`, so the
 * quantities already arrive in multiples of the capacity — everything here is
 * presentation over numbers that are already correct.
 *
 * All of it lives in one tested module because the same figures appear in the
 * ticket selector, the tier card, the cart summary and the totals; four
 * independent `quantity / capacity` expressions is how they drift apart.
 */

/**
 * Capacity of a table tier, or null when the tier is not a table.
 *
 * Deliberately unlike `parseTierIncrement`, which falls back to 1: an increment
 * of 1 is meaningful, whereas a "table of 1" is not — so null is the honest
 * answer and callers branch on it.
 */
export const parseTableCapacity = (value) => {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.floor(parsed);
};

export const isTableTier = (tier) =>
  parseTableCapacity(tier?.tableCapacity) !== null;

/** Seats → tables. Rounds up so a partial group never hides a seat. */
export const tableCount = (quantity, capacity) => {
  const seats = Number(quantity) || 0;
  const perTable = parseTableCapacity(capacity);
  if (!perTable || seats <= 0) return 0;
  return Math.ceil(seats / perTable);
};

/** Per-seat price → price of one whole table. */
export const tablePrice = (pricePerSeat, capacity) => {
  const perTable = parseTableCapacity(capacity);
  const price = Number(pricePerSeat) || 0;
  if (!perTable) return price;
  return price * perTable;
};

export const formatTableQuantity = (quantity, capacity) => {
  const perTable = parseTableCapacity(capacity);
  const seats = Number(quantity) || 0;
  if (!perTable) return String(seats);
  const tables = tableCount(seats, perTable);
  const tableWord = tables === 1 ? "table" : "tables";
  const seatWord = seats === 1 ? "seat" : "seats";
  return `${tables} ${tableWord} (${seats} ${seatWord})`;
};

/**
 * Messages for the machine-readable `code` the roster endpoints return with a
 * 409. Kept as data behind a single lookup so the copy cannot drift across the
 * components that display it.
 */
export const SEAT_ERROR_MESSAGES = {
  roster_locked: "The guest list for this table is closed.",
  seat_checked_in: "This guest has already arrived and can't be changed.",
  duplicate_guest: "This guest is already on another seat of this table.",
  guest_already_has_ticket: "This person already has a ticket for this event.",
  booking_unsettled: "This table isn't paid for yet.",
  reservation_canceled: "This table has been canceled.",
  email_unavailable:
    "That email address is already attached to another account.",
  missing_contact:
    "A guest needs at least an email address or a mobile number.",
  host_seat: "This seat can't be changed.",
  seat_has_no_ticket:
    "This table has no unused ticket left for this seat. Remove a guest to free one.",
};

const GENERIC_SEAT_ERROR = "Something went wrong. Please try again.";

/** code → message, then the server's own detail, then a generic fallback. */
export const seatErrorMessage = (error) => {
  const data = error?.response?.data;
  const mapped = data?.code ? SEAT_ERROR_MESSAGES[data.code] : null;
  if (mapped) return mapped;
  if (typeof data?.detail === "string" && data.detail) return data.detail;
  return GENERIC_SEAT_ERROR;
};

/**
 * ---------------------------------------------------------------------------
 * Seat expansion — the ONE conversion in the frontend.
 *
 * A table is a single product. The cart therefore holds a count of TABLES, not
 * seats: `quantity: 1` means one table. Every UI surface — the selector, the
 * counter, the summary, the totals, the per-cart cap — then deals in tables and
 * needs no special rule at all.
 *
 * Seats appear only where the API demands them, and there are exactly two such
 * boundaries, both of which validate quantity against tier_increment:
 *   - cart creation      (events/serializers/cart_serializer.py)
 *   - reservation submit (ReservationWithOrderEventCreateSerializer)
 *
 * An earlier design put seats in the cart instead, which forced every one of
 * those surfaces to divide by the capacity. Each place that was missed became a
 * bug. Hence one conversion, at the edge.
 * ---------------------------------------------------------------------------
 */

/** Tables -> seats, for the two API boundaries. Ordinary tiers pass through. */
export const seatsForTables = (tier, tableCount) => {
  const capacity = parseTableCapacity(tier?.tableCapacity);
  const count = Number(tableCount) || 0;
  if (!capacity) return count;
  return count * capacity;
};

/** Units one press of "+" adds to the cart: one table, or the tier increment. */
export const stepForTier = (tier, tierIncrement) =>
  isTableTier(tier) ? 1 : Number(tierIncrement) || 1;

/**
 * ---------------------------------------------------------------------------
 * Guest-step rules for a table's seats.
 * ---------------------------------------------------------------------------
 */

/** The tier behind a guest row, resolved from the tier list. */
export const tierForGuest = (guest, tierList = []) => {
  const tierId = guest?.ticket?.ticketTier ?? guest?.ticket?.value;
  if (tierId == null) return null;
  // Ids arrive as numbers from the API and as strings from cart keys, so
  // compare as strings rather than trusting either side.
  return (
    (tierList || []).find(
      (tier) =>
        String(tier?.id) === String(tierId) ||
        String(tier?.pk) === String(tierId),
    ) || null
  );
};

/** True when the host has typed nothing at all into this row. */
export const isBlankGuestRow = (guest) =>
  !guest?.firstName &&
  !guest?.lastName &&
  !guest?.email &&
  !guest?.phone &&
  !guest?.gender;

/**
 * A table is reserved now and its guests named later, so a table seat's row may
 * be left ENTIRELY blank -- the backend fills it with a placeholder.
 *
 * Blank is the rule rather than "optional fields": a half-filled row is still an
 * error, because a guest with a name but no phone cannot be sent their pass.
 */
export const guestDetailsAreOptional = (guest, tierList = []) =>
  isTableTier(tierForGuest(guest, tierList)) && isBlankGuestRow(guest);

/**
 * A table is a fixed block of seats, so its rows cannot be deleted.
 *
 * Deleting one decremented the cart quantity, which both changed the price of a
 * fixed-price product and left the seat count at 9 -- which the backend rejects,
 * since a table tier must be bought in multiples of its capacity
 * (validate_tier_purchase_quantity).
 */
export const guestRowIsFixed = (guest, tierList = []) =>
  isTableTier(tierForGuest(guest, tierList));

/** Capacity of the tier behind a cart key, or null when it is not a table. */
export const tableCapacityForTierId = (tierId, tierList = []) => {
  const tier = (tierList || []).find(
    (t) => String(t?.id) === String(tierId) || String(t?.pk) === String(tierId),
  );
  return parseTableCapacity(tier?.tableCapacity);
};

/**
 * How many guest ROWS a cart entry should start with.
 *
 * A table is a package: you buy one tier that happens to be N tickets, and you
 * may name as few as one guest now and the rest later on the roster screen. So
 * a table contributes ONE row, not one per seat -- while an ordinary tier still
 * needs a row per ticket, because those guests must all be named to be issued.
 */
export const initialGuestRows = (tierId, quantity, tierList = []) =>
  tableCapacityForTierId(tierId, tierList) ? 1 : Number(quantity) || 0;

/**
 * ---------------------------------------------------------------------------
 * The one badge a seat shows: what happened to that guest's pass.
 *
 * A named seat ALWAYS reports a delivery outcome. There is deliberately no
 * resting state that merely says the guest exists — "named" told the host
 * nothing they could act on, and the two situations hiding behind it needed
 * opposite responses: a failed send needs a resend, a missing ticket needs the
 * seat re-made.
 *
 * `seat.deliveryStatus` is the provider's own word, derived on the server from
 * TicketDeliveryLog (best outcome across WhatsApp and email): "sent",
 * "delivered", "opened", "read", "pending", "failed", "bounced" — plus
 * "no_ticket" for a seat that cannot be delivered to at all.
 *
 * `seat.status` is the seat's own lifecycle (empty → assigned → delivered →
 * claimed) and only decides the two states delivery cannot see: the guest opened
 * their pass, or walked through the door.
 * ---------------------------------------------------------------------------
 */
const DELIVERY_LABELS = {
  read: { label: "Opened", tone: "success", icon: "check" },
  opened: { label: "Opened", tone: "success", icon: "check" },
  delivered: { label: "Delivered", tone: "success", icon: "check" },
  sent: { label: "Sent", tone: "success", icon: "check" },
  pending: { label: "Sending…", tone: "pending" },
  failed: { label: "Failed", tone: "danger", icon: "alert" },
  bounced: { label: "Bounced", tone: "danger", icon: "alert" },
  no_ticket: { label: "No ticket", tone: "danger", icon: "alert" },
};

export const seatStateChip = (seat) => {
  if (!seat || !seat.name) return null;

  // Arrival outranks everything: they are in the room, so how the pass got to
  // them stopped mattering.
  if (seat.isCheckedIn)
    return { label: "Arrived", tone: "arrived", icon: "check" };
  // Claimed is stronger evidence than any provider status -- they opened it.
  if (seat.status === "claimed")
    return { label: "Opened", tone: "success", icon: "check" };

  const mapped = DELIVERY_LABELS[seat.deliveryStatus];
  if (mapped) return mapped;

  // The seat's own status is a fallback for a table whose delivery predates the
  // log, so a pass that demonstrably went out still reads as sent.
  if (seat.status === "delivered")
    return { label: "Sent", tone: "success", icon: "check" };

  // Named, and no attempt has reported yet. Amber, not neutral: something is
  // owed to this guest.
  return { label: "Not sent", tone: "pending" };
};
